import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Native push registration for the LessAlone Capacitor shell.
 *
 * Runs only on a native platform — `Capacitor.isNativePlatform()` is false
 * in any normal browser, so this becomes a no-op for the web SPA. On
 * Android (and later iOS) the registration token is POSTed to /api/devices
 * with the session cookie; the server fans push out next to email when a
 * new message lands or a 48h nudge fires.
 *
 * A tap on the system notification surfaces `data.deepLink` (set by the
 * server's `sendPush` payload) and routes the WebView to that path so the
 * user lands directly inside the conversation.
 */

/** Device-level push state from the app's perspective. */
export type PushPermissionState =
  /** Not on a native platform (e.g. a normal browser). */
  | 'unavailable'
  /** OS hasn't been asked yet — calling enablePushOnDevice will prompt. */
  | 'prompt'
  /** OS permission granted; the device should be registered. */
  | 'granted'
  /** OS permission denied; the user can only re-enable via OS settings. */
  | 'denied';

let registered = false;
let listenersBound = false;

/**
 * Reads the OS permission state without showing a dialog. Safe to call
 * from any render. Non-native platforms return 'unavailable'.
 */
export async function checkPushPermission(): Promise<PushPermissionState> {
  if (!Capacitor.isNativePlatform()) return 'unavailable';
  try {
    const status = await PushNotifications.checkPermissions();
    return mapStatus(status.receive);
  } catch {
    return 'unavailable';
  }
}

/**
 * Boot-time entry: ask for permission if undetermined, register on
 * success, no-op on a non-native platform. Called from App.tsx once the
 * user is signed in and onboarded so the OS dialog isn't a cold-launch
 * surprise.
 */
export async function initPushNotifications(): Promise<PushPermissionState> {
  if (!Capacitor.isNativePlatform()) return 'unavailable';
  if (registered) return 'granted';

  try {
    const status = await PushNotifications.requestPermissions();
    const state = mapStatus(status.receive);
    if (state !== 'granted') return state;
    await registerAndBindListeners();
    return 'granted';
  } catch (err) {
    console.warn('[push] init failed', err);
    return 'unavailable';
  }
}

/**
 * Explicit user-initiated request from the notification-settings page.
 * Same flow as initPushNotifications but always callable — useful when
 * the boot-time ask was dismissed and the user came back to enable later.
 * If the OS already denied the app, this returns 'denied' without
 * prompting (the user has to go to OS settings to re-enable).
 */
export async function enablePushOnDevice(): Promise<PushPermissionState> {
  return initPushNotifications();
}

function mapStatus(receive: string): PushPermissionState {
  switch (receive) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    case 'prompt':
    case 'prompt-with-rationale':
      return 'prompt';
    default:
      return 'unavailable';
  }
}

async function registerAndBindListeners(): Promise<void> {
  if (!listenersBound) {
    listenersBound = true;
    PushNotifications.addListener('registration', (token) => {
      void uploadToken(token.value).catch((err) => {
        console.warn('[push] upload failed', err);
      });
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[push] registrationError', err);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const deepLink = action.notification?.data?.deepLink;
      if (typeof deepLink === 'string' && deepLink.startsWith('/')) {
        window.location.assign(deepLink);
      }
    });
  }

  await PushNotifications.register();
  registered = true;
}

async function uploadToken(token: string): Promise<void> {
  await fetch('/api/devices', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform: 'android', token }),
  });
}

/**
 * Removes the current device's token on explicit sign-out. Best effort —
 * a network failure here doesn't block the sign-out flow.
 */
export async function unregisterPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    // The plugin doesn't surface the token after registration; the server
    // cleans up on next push attempt that fails with token_invalid. For
    // explicit cleanup we drop the registration on this device.
    await PushNotifications.removeAllListeners();
    await PushNotifications.unregister();
  } catch (err) {
    console.warn('[push] unregister failed', err);
  }
}
